'use client';

import { Fragment, type ReactNode } from 'react';

import Link from 'next/link';

import { LegalLink, legalDocumentIdFromHref } from '@/features/legal/legal-link';

// Minimal Markdown renderer for the legal pages (FR only). Supports the subset our
// legal documents use — ## / ### headings, paragraphs, "- " lists, **bold**,
// [label](/path) links and --- rules — with no external dependency.

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return parts
    .filter(Boolean)
    .map((part, index) => {
      const bold = /^\*\*([^*]+)\*\*$/.exec(part);
      if (bold) {
        return (
          <strong key={`${keyPrefix}-b${index}`} className="font-semibold text-slate-800">
            {bold[1]}
          </strong>
        );
      }
      const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
      if (link) {
        const [, label, href] = link;
        const className = 'font-semibold text-accent underline-offset-4 hover:underline';
        const legalDocumentId = legalDocumentIdFromHref(href);
        if (legalDocumentId) {
          return (
            <LegalLink key={`${keyPrefix}-l${index}`} doc={legalDocumentId} className={className}>
              {label}
            </LegalLink>
          );
        }
        if (href.startsWith('/')) {
          return (
            <Link key={`${keyPrefix}-l${index}`} href={href} className={className}>
              {label}
            </Link>
          );
        }
        return (
          <a key={`${keyPrefix}-l${index}`} href={href} className={className} target="_blank" rel="noreferrer">
            {label}
          </a>
        );
      }
      return <Fragment key={`${keyPrefix}-t${index}`}>{part}</Fragment>;
    });
}

export default function MarkdownLegal({ text }: { text: string }) {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let listItems: string[] = [];
  let paragraph: string[] = [];
  let key = 0;

  const flushParagraph = () => {
    if (!paragraph.length) {
      return;
    }
    const content = paragraph.join(' ');
    blocks.push(
      <p key={`p${key++}`} className="text-sm leading-7 text-slate-700">
        {renderInline(content, `p${key}`)}
      </p>,
    );
    paragraph = [];
  };

  const flushList = () => {
    if (!listItems.length) {
      return;
    }
    const items = listItems;
    blocks.push(
      <ul key={`ul${key++}`} className="list-disc space-y-1.5 pl-5 text-sm leading-7 text-slate-700">
        {items.map((item, index) => (
          <li key={index}>{renderInline(item, `li${key}-${index}`)}</li>
        ))}
      </ul>,
    );
    listItems = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushParagraph();
      flushList();
      continue;
    }
    if (line.startsWith('### ')) {
      flushParagraph();
      flushList();
      blocks.push(
        <h3 key={`h3${key++}`} className="mt-5 text-base font-semibold text-slate-800">
          {renderInline(line.slice(4), `h3${key}`)}
        </h3>,
      );
      continue;
    }
    if (line.startsWith('## ') || line.startsWith('# ')) {
      flushParagraph();
      flushList();
      const text2 = line.startsWith('## ') ? line.slice(3) : line.slice(2);
      blocks.push(
        <h2 key={`h2${key++}`} className="mt-6 text-[19px] font-bold text-slate-800">
          {renderInline(text2, `h2${key}`)}
        </h2>,
      );
      continue;
    }
    if (line === '---') {
      flushParagraph();
      flushList();
      blocks.push(<hr key={`hr${key++}`} className="my-5 border-slate-200" />);
      continue;
    }
    if (/^[-*] /.test(line)) {
      flushParagraph();
      listItems.push(line.slice(2));
      continue;
    }
    paragraph.push(line.trim());
  }
  flushParagraph();
  flushList();

  return <div className="space-y-3">{blocks}</div>;
}
